/**
 * Node Registry
 * All nodes now use the unified GenericNode component for consistent styling
 * 
 * This file exports all node types for ReactFlow.
 * The GenericNode component handles all visual rendering based on nodeDefinitions.
 */

// Generic node for all node types
import GenericNode from './GenericNode';

// PLOP_INJECT_IMPORT - New modular nodes will be imported here (if needed for custom behavior)
import LabelNode from './LabelNode';
import SwitchNode from './SwitchNode';

export const nodeTypes = {
  // ===== TRIGGERS =====
  trigger: GenericNode,
  manual: GenericNode,
  'manual-trigger': GenericNode,
  schedule: GenericNode,
  'schedule-trigger': GenericNode,
  'webhook-trigger': GenericNode,
  'chatTrigger': GenericNode,
  'chat-trigger': GenericNode,
  'mcp-trigger': GenericNode,
  'microsoft-outlook-trigger': GenericNode,
  'polling-trigger': GenericNode,
  'execute-workflow': GenericNode,
  'error-trigger': GenericNode,

  // ===== ACTIONS =====
  http: GenericNode,
  'http-request': GenericNode,
  graphql: GenericNode,
  email: GenericNode,
  database: GenericNode,
  api: GenericNode,
  'execute-workflow-action': GenericNode,
  
  // ===== AI & ML =====
  'ai-agent': GenericNode,
  'ai-batch': GenericNode,
  openai: GenericNode,
  anthropic: GenericNode,
  'text-analysis': GenericNode,
  'kbSearch': GenericNode,
  'kbUpload': GenericNode,
  
  // AI Sub-Nodes
  'access-agent-builder': GenericNode,
  'access-llm': GenericNode,
  'claude-chat-model': GenericNode,
  'azure-openai': GenericNode,
  'simpleMemory': GenericNode,
  'simple-memory': GenericNode,
  'chat-memory-manager': GenericNode,
  'agentOutputParser': GenericNode,
  
  // ===== FLOW CONTROL =====
  conditional: GenericNode,
  if: GenericNode,
  switch: SwitchNode,
  merge: GenericNode,
  loop: GenericNode,

  wait: GenericNode,
  
  // ===== DATA TRANSFORMATION =====
  transform: GenericNode,
  set: GenericNode,
  'edit-fields': GenericNode,
  filter: GenericNode,
  sort: GenericNode,
  limit: GenericNode,
  'remove-duplicates': GenericNode,
  aggregate: GenericNode,
  summarise: GenericNode,
  json: GenericNode,
  xml: GenericNode,
  'json-to-csv': GenericNode,
  'csv-to-json': GenericNode,
  'binary-to-text': GenericNode,
  'text-to-binary': GenericNode,
  'htmlExtract': GenericNode,
  
  // ===== UTILITIES =====
  code: GenericNode,
  function: GenericNode,
  datetime: GenericNode,
  string: GenericNode,
  'convert-to-html': GenericNode,
  'compare-datasets': GenericNode,
  'crypto': GenericNode,
  'no-op': GenericNode,
  'split-in-batches': GenericNode,
  'stop-execution': GenericNode,

  // ===== CREDENTIALS / AUTH =====
  oauth2: GenericNode,
  'api-key': GenericNode,
  'basic-auth': GenericNode,
  jwt: GenericNode,
  
  // ===== FILES =====
  'ftp': GenericNode,
  'azure-blob-storage': GenericNode,
  'html-to-pdf': GenericNode,
  'html-template': GenericNode,
  'report-hub': GenericNode,

  // ===== DATABASE NODES =====
  'data-table': GenericNode,
  'databricks': GenericNode,
  'MsSQL': GenericNode,
  'ms-sql': GenericNode, // Legacy alias for backward compatibility
  'sql-server': GenericNode, // Legacy alias for backward compatibility
  'postgresql': GenericNode,
  'mongodb': GenericNode,
  'mysql': GenericNode,
  'turso': GenericNode,
  'redis': GenericNode,
  'elasticsearch': GenericNode,
  
  // ===== ACCESS INTEGRATIONS =====
  'access-mail': GenericNode,
  'access-sms': GenericNode,
  'access-charity-crm': GenericNode,
  'access-financials': GenericNode,
  'access-financials-mcp': GenericNode,
  'access-kanban': GenericNode,
  'access-payroll': GenericNode,
  'access-ap': GenericNode,
  'access-ar': GenericNode,
  'access-gl': GenericNode,
  'access-budgeting': GenericNode,
  'access-reporting': GenericNode,
  'access-crm': GenericNode,
  'access-dimensions': GenericNode,
  'access-workspace': GenericNode,
  'access-agentic-platform': GenericNode,
  'access-collect': GenericNode,
  'access-document': GenericNode,
  'access-donorfy': GenericNode,
  'access-evo': GenericNode,
  'access-paycircle': GenericNode,
  'access-peoplehr': GenericNode,
  'data-engine-api': GenericNode,
  'smtp-email': GenericNode,
  'sendgrid': GenericNode,
  'evo-web-feed': GenericNode,

  // ===== EXTERNAL INTEGRATIONS =====
  'microsoft-teams': GenericNode,
  'microsoft-outlook': GenericNode,
  'microsoft-onedrive': GenericNode,
  'microsoft-sharepoint': GenericNode,
  'microsoft-entra': GenericNode,
  'asana': GenericNode,

  // ===== EXTERNAL INTEGRATION TRIGGERS =====
  'asana-trigger': GenericNode,
  'asana-webhook-trigger': GenericNode,

  // ===== LANGCHAIN & RAG =====
  'langchain-vector-store': GenericNode,
  'langchain-document-loader': GenericNode,
  'langchain-text-splitter': GenericNode,
  'langchain-rag-chain': GenericNode,
  'langchain-chat-memory': GenericNode,
  'langchain-embeddings': GenericNode,
  'langchain-prompt-template': GenericNode,
  
  // ===== RESPONSES =====
  'respond-to-trigger': GenericNode,
  // Backward compatibility alias
  'respond-to-chat': GenericNode,
  
  // ===== TOOLS =====
  'mcp-tool': GenericNode,
  'mcp-ui-renderer': GenericNode,
  'brave-search': GenericNode,
  'execute-workflow-tool': GenericNode,
  
  // ===== SPECIAL NODES =====
  // Label keeps its own component for special textarea behavior
  'label': LabelNode,
  
  // PLOP_INJECT_EXPORT - New modular nodes will be registered here
};
